'use client'
import { createTheme as createMuiTheme, type Theme } from '@mui/material/styles'
import { MuiAlert, MuiAlertTitle } from './components/alert'
import { MuiAppBar, MuiToolbar } from './components/appBar'
import { MuiBadge } from './components/badge'
import {
  MuiBottomNavigation,
  MuiBottomNavigationAction,
} from './components/bottomNavigation'
import { MuiButton, MuiIconButton } from './components/button'
import { MuiCheckbox } from './components/checkbox'
import { MuiCircularProgress } from './components/circularProgress'
import { MuiBackdrop, MuiDialog, MuiDialogTitle } from './components/dialog'
import { MuiDivider } from './components/divider'
import { MuiDrawer } from './components/drawer'
import { MuiFormControlLabel } from './components/formControlLabel'
import { MuiLinearProgress } from './components/linearProgress'
import {
  MuiList,
  MuiListItem,
  MuiListItemIcon,
  MuiListItemText,
} from './components/list'
import { MuiMenu, MuiMenuItem } from './components/menu'
import { MuiModal } from './components/modal'
import { MuiPaper } from './components/paper'
import { MuiPopover } from './components/popover'
import { MuiRadio } from './components/radio'
import { MuiSelect } from './components/select'
import { MuiSlider } from './components/slider'
import { MuiSnackbar, MuiSnackbarContent } from './components/snackbar'
import { MuiSvgIcon } from './components/svgIcon'
import { MuiSwitch } from './components/switch'
import { MuiTab, MuiTabs } from './components/tab'
import {
  MuiPaginationItem,
  MuiTableBody,
  MuiTableCell,
  MuiTablePagination,
  MuiTableRow,
  MuiTableSortLabel,
} from './components/table'
import {
  MuiFormHelperText,
  MuiFormLabel,
  MuiInputAdornment,
  MuiInputBase,
  MuiInputLabel,
  MuiOutlinedInput,
  MuiTextField,
} from './components/textField'
import { MuiTooltip } from './components/tooltip'
import { MuiTypography } from './components/typography'
import { createUseMediaQuery } from './components/useMediaQuery'
import { MuiCssBaseline } from './system/baseline'
import { themeModeToElevations } from './system/elevations'
import { themeModeToPalette } from './system/palette'
import { typography } from './system/typography'
import type { ThemeModes } from './types'

type ThemeUniqueKey = ThemeModes
type CreateTheme = (mode?: ThemeModes) => Theme
const THEME_CACHE: Partial<Record<ThemeUniqueKey, Theme>> = {}

// Create a theme instance.
export const createTheme: CreateTheme = (mode = 'light') => {
  const themeUniqueKey: ThemeUniqueKey = mode
  if (THEME_CACHE[themeUniqueKey]) {
    return THEME_CACHE[themeUniqueKey]
  }

  const initialTheme = createMuiTheme({
    typography,
    // cssVariables: true,
    direction: 'rtl',
    palette: themeModeToPalette[mode],
    elevations: themeModeToElevations[mode],
    spacing__SCF: {
      spacingNone: '0px',
      spacingXXXS: '2px',
      spacingXXS: '4px',
      spacingXS: '8px',
      spacingS: '12px',
      spacingM: '16px',
      spacingL: '24px',
      spacingXL: '32px',
      spacingXXL: '40px',
      spacingXXXL: '48px',
    },
    strokeWidth__SCF: {
      none: '0px',
      thin: '1px',
      thick: '2px',
      thicker: '4px',
    },
    cornerRadius__SCF: {
      cornerRadiusNone: '0px',
      cornerRadiusS: '4px',
      cornerRadiusM: '8px',
      cornerRadiusL: '12px',
      cornerRadiusCircular: '1000px',
      cornerRadiusMNudge: '10px',
      cornerRadiusXS: '2px',
      cornerRadiusSNudge: '6px',
      cornerRadiusXL: '16px',
    },
  })
  const createdTheme = createMuiTheme(initialTheme, {
    components: {
      MuiCssBaseline: MuiCssBaseline(initialTheme),
      MuiButton: MuiButton(initialTheme),
      MuiIconButton: MuiIconButton(initialTheme),
      MuiAppBar: MuiAppBar(initialTheme),
      MuiToolbar: MuiToolbar(initialTheme),
      MuiSnackbar,
      MuiSnackbarContent: MuiSnackbarContent(initialTheme),
      MuiSvgIcon: MuiSvgIcon(initialTheme),
      MuiTextField: MuiTextField(initialTheme),
      MuiInputLabel: MuiInputLabel(initialTheme),
      MuiFormLabel,
      MuiOutlinedInput: MuiOutlinedInput(initialTheme),
      MuiInputAdornment: MuiInputAdornment(initialTheme),
      MuiFormHelperText: MuiFormHelperText(initialTheme),
      MuiModal,
      MuiSelect: MuiSelect(initialTheme),
      MuiMenu: MuiMenu(initialTheme),
      MuiMenuItem: MuiMenuItem(initialTheme),
      MuiAlert: MuiAlert(initialTheme),
      MuiAlertTitle: MuiAlertTitle(initialTheme),
      MuiPaper: MuiPaper(initialTheme),
      MuiCheckbox,
      MuiFormControlLabel: MuiFormControlLabel(initialTheme),
      MuiLinearProgress: MuiLinearProgress(initialTheme),
      MuiBottomNavigation: MuiBottomNavigation(initialTheme),
      MuiBottomNavigationAction: MuiBottomNavigationAction(initialTheme),
      MuiTableCell: MuiTableCell(initialTheme),
      MuiTableRow,
      MuiTableSortLabel: MuiTableSortLabel(initialTheme),
      MuiTablePagination: MuiTablePagination(initialTheme),
      MuiPaginationItem: MuiPaginationItem(initialTheme),
      MuiTableBody: MuiTableBody(initialTheme),
      MuiDialog: MuiDialog(initialTheme),
      MuiDialogTitle: MuiDialogTitle(initialTheme),
      MuiBackdrop: MuiBackdrop(initialTheme),
      MuiList,
      MuiListItem,
      MuiListItemIcon: MuiListItemIcon(initialTheme),
      MuiListItemText: MuiListItemText(initialTheme),
      MuiDivider: MuiDivider(initialTheme),
      MuiSlider: MuiSlider(initialTheme),
      MuiBadge: MuiBadge(initialTheme),
      MuiUseMediaQuery: createUseMediaQuery(initialTheme),
      MuiInputBase: MuiInputBase(initialTheme),
      MuiSwitch: MuiSwitch(initialTheme),
      MuiTab: MuiTab(initialTheme),
      MuiTabs: MuiTabs(initialTheme),
      MuiTooltip: MuiTooltip(initialTheme),
      MuiRadio,
      MuiCircularProgress,
      MuiPopover,
      MuiDrawer,
      MuiTypography,
    },
  })
  THEME_CACHE[themeUniqueKey] = createdTheme

  return THEME_CACHE[themeUniqueKey]
}

export const theme = createTheme()
